import { Box, LinearProgress, Typography, alpha, useTheme } from '@mui/material'
import { SeverityBadge, SEV, SEV_INK } from './boardBadges'

const SeverityChip = ({ label, value, color }) => {
  const short =
    label === 'Critical' || label === 'C'
      ? 'C'
      : label === 'High' || label === 'H'
        ? 'H'
        : label === 'Medium' || label === 'M'
          ? 'M'
          : 'L'
  const ink =
    color === SEV.critical
      ? SEV_INK.critical
      : color === SEV.high
        ? SEV_INK.high
        : color === SEV.medium
          ? SEV_INK.medium
          : SEV_INK.low
  return (
    <SeverityBadge
      dense
      shortLabel={short}
      label={label}
      value={value}
      color={color}
      ink={ink}
    />
  )
}

const DtSummaryStrip = ({
  summary = {},
  mappingsReady = false,
  mappingsLoading = false
}) => {
  const theme = useTheme()
  const dd = theme.custom.colors.dataDrawer
  const vuln = theme.custom.colors.supportTypes.vulnerability
  const sev = summary.severity || {}

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        border: `1px solid ${alpha(vuln, 0.35)}`,
        backgroundColor: alpha(dd.surface, 0.85),
        backgroundImage: `linear-gradient(100deg, ${alpha(vuln, 0.12)} 0%, transparent 65%)`,
        boxShadow: `inset 0 1px 0 ${alpha('#fff', 0.04)}`
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 1.5, sm: 2.5 },
          alignItems: 'flex-start',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, sm: 2.5 } }}>
          <Stat label='Repositories' value={mappingsReady ? summary.repoCount : '—'} muted={!mappingsReady} />
          <Stat label='DT projects' value={summary.projectCount ?? 0} />
          <Stat label='Orphans' value={summary.orphanCount ?? 0} accent={vuln} />
          <Stat label='Findings' value={summary.totalFindings ?? 0} />
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
          <SeverityChip label='Critical' value={sev.critical} color='#ff1744' />
          <SeverityChip label='High' value={sev.high} color='#f33b59' />
          <SeverityChip label='Medium' value={sev.medium} color='#ff9800' />
          <SeverityChip label='Low' value={sev.low} color='#ffd54f' />
        </Box>
      </Box>
      {mappingsLoading && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant='caption' sx={{ color: dd.muted, display: 'block', mb: 0.75 }}>
            Mapping repositories via Bitbucket…
          </Typography>
          <LinearProgress
            variant='indeterminate'
            sx={{
              height: 3,
              borderRadius: 1,
              backgroundColor: alpha(vuln, 0.15),
              '& .MuiLinearProgress-bar': { backgroundColor: vuln }
            }}
          />
        </Box>
      )}
    </Box>
  )
}

const Stat = ({ label, value, muted = false, accent }) => {
  const theme = useTheme()
  const dd = theme.custom.colors.dataDrawer
  return (
    <Box>
      <Typography
        variant='caption'
        sx={{
          color: dd.muted,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          display: 'block'
        }}
      >
        {label}
      </Typography>
      <Typography
        variant='h6'
        sx={{
          fontWeight: 800,
          lineHeight: 1.2,
          color: muted ? dd.muted : (accent || dd.text)
        }}
      >
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Typography>
    </Box>
  )
}

export { DtSummaryStrip, SeverityChip }
